import dayjs from "dayjs";
import { cloneDeep, orderBy } from "lodash";
import i18n from "i18next";

// Const
import { timeFormat } from "const/nuclias/dateFormat";
import { organizationType } from "const/nuclias/organization";
import { userType, userPrivilege } from "const/nuclias/user";

// Util
import { formatDateFromTimestamp } from "utils/date-time.util";

import {
  defaultAccessPrivilege,
  defaultAccessLevel,
  defaultManagedList,
  defaultLastAccessTime,
} from "../const";

import accountExtension from "./extensions/account";

class AccountModel {
  static accountState;

  id = 0;
  checked = false;
  name = '';
  email = '';
  accessPrivilege = '';
  accessPrivilegeLabel = '';
  accessLevelLabel = '';
  accessLevelOwner = '';
  managedSiList = [];
  managedSiteList = [];
  status = false;
  statusLabel = '';
  lastAccessTime = '';
  lastAccessTimestamp = 0;
  rawData = {};

  constructor(rawData, selectedOrg, orgTreeMap) {
    this.id = rawData.id;
    this.name = rawData.name;
    this.email = rawData.email;
    this.status = rawData.registerStatus;
    this.statusLabel = this.status ? '3f68e67dc6' : 'd5ca088299';
    this.statusTLabel = i18n.t(this.statusLabel);

    const accessPrivilege = this._setAccessPrivilege(rawData);
    this.accessPrivilege = accessPrivilege.type;
    this.accessPrivilegeLabel = accessPrivilege.typeLabel;
    this.accessPrivilegeTLabel = i18n.t(accessPrivilege.typeLabel);

    const accessLevel = this._setAccessLevel(rawData, selectedOrg);
    this.accessLevelType = accessLevel.type;
    this.accessLevelLabel = accessLevel.typeLabel;
    this.accessLevelOwner = accessLevel.owner;

    const managedScopeList = this._setManagedScopeList(rawData, selectedOrg, orgTreeMap);
    this.managedSiList = managedScopeList.siList;
    this.managedSiteList = managedScopeList.siteList;

    const lastAccessTime = this._setLastAccessTime(rawData);
    this.lastAccessTime = lastAccessTime.datetime;
    this.lastAccessTimestamp = lastAccessTime.timestamp;

    this.rawData = rawData;
  }

  _setAccessLevel(rawData, selectedOrg) {
    const accessLevel = cloneDeep(defaultAccessLevel);
    accessLevel.type = rawData.type;

    switch (rawData.type) {
      case userType.sp:
        accessLevel.owner = selectedOrg.name;
        accessLevel.typeLabel = 'e1b3ec89ea';
        break;
      case userType.si:
        accessLevel.owner = selectedOrg.name;
        accessLevel.typeLabel = '4b65444060';
        break;
      case userType.org:
        accessLevel.owner = selectedOrg.name;
        accessLevel.typeLabel = 'c1ca926603';
        break;
      case userType.siteTag:
        accessLevel.owner = rawData.manageResources[0].name;
        accessLevel.typeLabel = 'c1ca926603';
        break;
      case userType.site:
        accessLevel.owner = rawData.manageResources[0].name;
        accessLevel.typeLabel = 'c1ca926603';
        break;
      default:
        return null;
    }

    return accessLevel;
  }

  _setAccessPrivilege(rawData) {
    const accessPrivilege = cloneDeep(defaultAccessPrivilege);

    accessPrivilege.type = rawData.privilege;

    switch (rawData.privilege) {
      case userPrivilege.manager:
        accessPrivilege.type = 'Admin';
        accessPrivilege.typeLabel = 'e3afed0047';
        break;
      case userPrivilege.editor:
        accessPrivilege.type = 'Editor';
        accessPrivilege.typeLabel = '344a7f427f';
        break;
      case userPrivilege.monitor:
        accessPrivilege.type = 'Monitor';
        accessPrivilege.typeLabel = 'd2986ac8cb';
        break;
      case userPrivilege.viewer:
        accessPrivilege.type = 'Viewer';
        accessPrivilege.typeLabel = 'fb15c53f22';
        break;
      default:
        return;
    }

    return accessPrivilege;
  }

  _setManagedScopeList(rawData, selectedOrg, orgTreeMap) {
    const managedScopeList = cloneDeep(defaultManagedList);

    if (rawData.type === userType.sp) {
      for (const siOrg of selectedOrg.children) {
        managedScopeList.siList.push(siOrg.name);
      }
    }

    if (rawData.type === userType.org) {
      for (const siteId in selectedOrg.storeObj) {
        managedScopeList.siteList.push(selectedOrg.storeObj[siteId].name);
      }
    }

    if (rawData.type === userType.siteTag) {
      let siteTag = null;

      const entries = Object.entries(selectedOrg.storeTagObj);
      siteTag = entries.filter(entrie => {
        return +entrie[0] === rawData.manageResources[0].id
      })[0][1];

      for (let siteId in orgTreeMap.withStore) {
        if (orgTreeMap.withStore[siteId].indexOf(siteTag.id) > -1) {
          managedScopeList.siteList.push(selectedOrg.storeObj[siteId].name);
        }
      }
    }

    if (rawData.type === userType.site) {
      managedScopeList.siteList.push(selectedOrg.storeObj[rawData.manageResources[0].id].name);
    }

    managedScopeList.siList = orderBy(managedScopeList.siList);
    managedScopeList.siteList = orderBy(managedScopeList.siteList);

    return managedScopeList;
  }

  _setLastAccessTime(rawData) {
    const lastAccessTime = cloneDeep(defaultLastAccessTime);

    if (!rawData?.lastAccessTime) {
      return lastAccessTime;
    }

    lastAccessTime.datetime = formatDateFromTimestamp(dayjs.utc(rawData.lastAccessTime), timeFormat.yDateAndTime);
    lastAccessTime.timestamp = dayjs.utc(rawData.lastAccessTime).unix();

    return lastAccessTime;
  }

  get managedScopeLength() {
    if (this.accessLevelType === organizationType.sp || this.accessLevelType === organizationType.si) {
      return this.managedSiList.length;
    } else {
      return this.managedSiteList.length;
    }
  }

  get managedScope() {
    if (this.accessLevelType === organizationType.sp || this.accessLevelType === organizationType.si) {
      return this.managedSiList;
    } else {
      return this.managedSiteList;
    }
  }

  toggleChecked() {
    this.checked = !this.checked;
  }
}

for (const funcName in accountExtension) {
  AccountModel[funcName] = accountExtension[funcName];
}

export default AccountModel;